<template>
  <div
    id="alert-sidebar"
    v-shortkey="['esc']"
    class="sidepanel"
    @shortkey="close"
  >
    <div class="sidepanel-topbar">
      <div class="sidepanel-topbar__title">
        Alerts
      </div>
      <span
        id="exit-sidepanel"
        class="sidepanel-exit"
        @click="close"
        v-html="closeIcon"
      />
    </div>
    <template v-if="fetching">
      <div class="empty-state">
        <loader :size="50" />
      </div>
    </template>
    <template v-else-if="alerts.length > 0">
      <div class="sidepanel-items">
        <template v-for="(alert, index) in alerts">
          <div
            :id="'alert-item-' + index"
            :key="index"
            :class="[getAlertClass(alert.level), 'alert-item']"
          >
            <span
              class="alert-item-icon"
              v-html="getAlertIcon(alert.level)"
            />
            <div class="alert-item-message-container">
              <div
                v-tooltip="{ content: alert.message }"
                class="alert-item-message"
              >
                {{ alert.message }}
              </div>
              <div class="alert-item-message alert-item-message--submessage">
                {{ alert.source }}
              </div>
              <div class="alert-item-timestamp">
                {{ alert.timestamp | date }}
              </div>
            </div>
          </div>
        </template>
      </div>
      <div class="alert-footer">
        <pagination
          class="m-0"
          :show-per-page="false"
          :page.sync="alertPaginationParams.page"
          :per-page.sync="alertPaginationParams.perPage"
          :total-rows.sync="totalAlerts"
          :rows-per-page-label="'Alerts per page'"
        />
      </div>
    </template>
    <template v-else>
      <div class="empty-state">
        There are no new alerts at this time.
      </div>
    </template>
  </div>
</template>

<script>
import feather from 'feather-icons'
import notificationQueries from '@/graphql/notifications.js'
import Pagination from '@/components/pagination.vue'
import Loader from '@/components/loader.vue'

export default {
  name: 'EventSidePanel',
  components: {
    Pagination,
    Loader,
  },
  mixins: [notificationQueries],
  data() {
    return {
      fetching: false,
      alerts: [],
      totalAlerts: 4,
      alertPaginationParams: {
        page: 1,
        perPage: 10,
      },
      alertLevel: {
        info: {
          class: 'alert-item--blue',
          icon: feather.icons['alert-circle'].toSvg(),
        },
        warn: {
          class: 'alert-item--yellow',
          icon: feather.icons['alert-triangle'].toSvg(),
        },
        critical: {
          class: 'alert-item--red',
          icon: feather.icons['alert-octagon'].toSvg(),
        },
      },
      closeIcon: feather.icons.x.toSvg(),
    }
  },
  watch: {
    alertPaginationParams: {
      handler() {
        this.fetchAlerts()
      },
      deep: true,
    },
  },
  created() {
    this.fetchAlerts()
  },
  methods: {
    close() {
      this.$emit('close')
    },
    async fetchAlerts() {
      this.fetching = true
      try {
        const _result = await this.listNotificationsQuery(this.alertPaginationParams)
        const {alertsList, totalRows} = _result
        this.fetching = false
        this.alerts = alertsList
        this.totalAlerts = totalRows
      } catch (error) {
        this.fetching = false
        this.displayAlert('fail', 'Unable to fetch alerts.')
        console.log(error)
      }
    },
    getAlertClass(level) {
      const _defaultClass = this.alertLevel.info.class
      return level ? this.alertLevel[level].class : _defaultClass
    },
    getAlertIcon(level) {
      const _defaultIcon = this.alertLevel.info.icon
      return level ? this.alertLevel[level].icon : _defaultIcon
    },
  },
}
</script>

<style lang="scss" scoped>
.alert {
  &-item {
    @include flex-display();
    @include flex-align-items(center);
    width: 100%;
    box-sizing: border-box;
    border-radius: 5px;
    padding: 1em;
    border: 1px solid $light-grey;
    color: $black;
    margin-bottom: 1em;
    &--red {
      border-color: $red;
    }
    &--yellow {
      border-color: $yellow;
    }
    &--blue {
      border-color: $blue;
    }
    &-message {
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      &::first-letter {
        text-transform: capitalize;
      }
      &--submessage {
        font-weight: 400;
        font-size: $small-font;
        color: $dark-grey;
      }
      &-container {
        @include flex-display();
        @include flex-direction(column);
        width: 90%;
      }
    }
    &-icon {
      @include flex-display();
      @include flex-align-items(center);
      @include flex-justify-content(center);
      width: 35px;
      height: 35px;
      margin-right: 1em;
      color: $dark-grey;
    }
    &-timestamp {
      color: $grey-text;
      font-size: $small-font;
      font-weight: 400;
    }
  }
}
</style>
